import React, { useEffect, useState } from 'react';
import { cloneDeep, get } from 'lodash';
import { observer } from 'mobx-react-lite';
import { useNavigate } from '@thd-olt-component-react/router';
import { jobPostingValidators } from '../utils/validations';
import { JOB_POSTING_STATUS, EXP_ROUTES, CREATE_JOB_POSTING_DEFAULTS } from '../utils/constants';
import { useStore } from '../store/index';
import { capitalizesFirstLetters } from '../utils/formatters';

export const JobPostingContext = React.createContext({});

export const JobPostingProvider = ({ children }) => {

  const [editorModeActive, setEditorMode] = useState(false);
  const [isJobPostingFetching, setIsJobPostingFetching] = useState(false);
  const [activeJobPosting, setActiveJobPosting] = useState({});
  const [editedJobPosting, setEditedJobPosting] = useState({});
  const [savingJobPostingMode, setSavingJobPostingMode] = useState(null);
  const [hasTemplate, setHasTemplate] = useState(false);

  const {
    jobPostingStore: {
      getJobPosting: getJobPostingStore,
      saveJobPosting: saveJobPostingStore,
      updateJobPostingStatus: updateJobPostingStatusStore,
      togglePublishJobPostingModal
    }
  } = useStore();

  const navigate = useNavigate();

  const getJobPosting = async (jobPostingId) => {
    setIsJobPostingFetching(true);
    const data = await getJobPostingStore(jobPostingId);
    setActiveJobPosting(data);
    if (editorModeActive) {
      setEditedJobPosting(data);
    }
    setIsJobPostingFetching(false);
  };

  const createNewJobPosting = (
    jobDescription,
    educationAndSkills,
    tradeType,
    yearsOfExperience,
    companyName,
    companyURL,
    companyDescription
  ) => {
    setEditedJobPosting({
      companyName: companyName || '',
      companyUrl: companyURL || '',
      companyDescription: companyDescription || '',
      locationText: '',
      educationAndSkills: educationAndSkills || '',
      title: '',
      jobDescription: jobDescription || ' ',
      tradeType: tradeType || CREATE_JOB_POSTING_DEFAULTS.TRADE_TYPE_BASIC_HEADER,
      employmentType: CREATE_JOB_POSTING_DEFAULTS.EMPLOYMENT_TYPE_BASIC_HEADER,
      yearsOfExperience: yearsOfExperience || CREATE_JOB_POSTING_DEFAULTS.EXPERIENCE_LEVEL_BASIC_HEADER,
      payFrequency: CREATE_JOB_POSTING_DEFAULTS.PAY_FREQUENCY_BASIC_HEADER,
      payRange: { minAmount: '', maxAmount: '' },
      benefits: { choices: [], other: '' },
      deadlineToApply: null
    });
    setEditorMode(true);
  };

  useEffect(() => {
    if (editorModeActive) {
      setEditedJobPosting({ ...cloneActiveJobPosting() });
    } else {
      setEditedJobPosting({});
    }
  }, [editorModeActive, isJobPostingFetching]);

  function updateJobPostingField(key, value) {
    setEditedJobPosting((previousEditedPosting) => ({ ...previousEditedPosting, ...{ [key]: value } }));
  }

  async function saveJobPosting(publish) {
    let canSave;

    if (publish) {
      setSavingJobPostingMode('PUBLISH');
      canSave = canPublishJobPosting();
    } else {
      setSavingJobPostingMode('DRAFT');
      canSave = canSaveAsDraft();
    }

    if (canSave) {
      let jobPosting = editorModeActive ? modifyJobPosting(editedJobPosting) : activeJobPosting;
      jobPosting = { ...jobPosting,
        status: (publish ? 'ACTIVE' : 'DRAFT'),
        companyUrl: jobPosting.companyUrl || null };
      setIsJobPostingFetching(true);
      await saveJobPostingStore(jobPosting, togglePublishJobPostingModal);
      setIsJobPostingFetching(false);
      updateActiveJobPosting(publish, jobPosting);
      // set delay so useEffects are allowed to run
      window.setTimeout(() => {
        setSavingJobPostingMode('');
        navigate(EXP_ROUTES.JOB_POSTING(activeJobPosting.jobPostingId));
      }, 250);
    }
  }

  const updateJobPostingStatus = async (jobPostingId, status, jobPostingPage, toggleDialogCb, toggleEdit) => {
    await updateJobPostingStatusStore(jobPostingId, status, jobPostingPage, toggleDialogCb);
    setActiveJobPosting({
      ...activeJobPosting,
      status
    });
    // set delay so useEffects are allowed to run
    window.setTimeout(() => {
      setSavingJobPostingMode('');
      if (editorModeActive || toggleEdit) {
        toggleEditorMode();
      }
      navigate(EXP_ROUTES.JOB_POSTING(jobPostingId));
    }, 250);
  };

  function modifyJobPosting(jobPosting) {
    return {
      ...jobPosting,
      title: capitalizesFirstLetters(jobPosting.title),
      // replace to add breaks to the end of <p> tags because WS does not render them correctly
      jobDescription: jobPosting.jobDescription?.replace(/<\/p>(<br>)?/g, '</p><br>')
    };
  }

  function updateActiveJobPosting(isPublishing, jobPosting) {
    setActiveJobPosting(() => {
      setEditorMode(false);
      setSavingJobPostingMode(false);
      return ({
        ...jobPosting,
        ...isPublishing ? { status: JOB_POSTING_STATUS.ACTIVE } : { status: JOB_POSTING_STATUS.DRAFT }
      });
    }
    );
  }

  function toggleEditorMode() {
    setEditorMode((state) => {
      return !state;
    });
  }

  function shouldRenderJobPostingField(attr) {
    return !!get(activeJobPosting, attr) || editorModeActive;
  }

  function canPublishJobPosting() {
    const jobPosting = editorModeActive ? editedJobPosting : activeJobPosting;
    return Object.values(jobPostingValidators).every((it) => {
      return (it(jobPosting));
    });
  }

  function canSaveAsDraft() {
    return canPublishJobPosting();
    // fix for various draft save issues
    // const jobPosting = editorModeActive ? editedJobPosting : activeJobPosting;
    // return Object.values(jobPostingDraftValidationKeys).every((key) => {
    //   return jobPostingValidators[key](jobPosting);
    // });
  }

  return (
    <JobPostingContext.Provider value={{
      editorModeActive,
      toggleEditorMode,
      activeJobPosting: editorModeActive ? editedJobPosting : activeJobPosting,
      isJobPostingFetching,
      getJobPosting,
      createNewJobPosting,
      shouldRenderJobPostingField,
      updateJobPostingField,
      saveJobPosting,
      editedJobPosting,
      canPublishJobPosting,
      canSaveAsDraft,
      savingJobPostingMode,
      updateJobPostingStatus,
      setEditedJobPosting,
      hasTemplate,
      setHasTemplate
    }}
    >
      {children}
    </JobPostingContext.Provider>
  );

  function cloneActiveJobPosting() {
    return cloneDeep(activeJobPosting);
  }

};

export default observer(JobPostingProvider);
